<template>
  <div
    v-if="source"
    v-tippy="{
      arrow: false,
      animation: 'fade',
      duration: 200,
      delay: [200, 0],
      content: `Synced from ${source.name}`,
    }"
    class="event-source-icon"
  >
    <component :is="source.icon" />
  </div>
</template>

<script lang="ts">
import {
  Component,
  computed,
  defineComponent,
  markRaw,
  PropType,
  toRefs,
} from "vue";

import type { Calendar } from "@ctrl/controller-rpc";

import GoogleCalendarIcon from "@/components/icons/apps/google/GoogleCalendarIcon.vue";

/** The service a calendar — and thus its events — synchronizes from. */
type CalendarSource = { name: string; icon: Component };

/**
 * Resolves the source service of a calendar's events.
 *
 * Today every calendar connection is a Google account (`Calendar.connection`
 * is a `GoogleAccountId`, and Google Calendar is the only calendar
 * synchronization the controller supports), so every event resolves to
 * Google Calendar. When other calendar providers land (e.g. Microsoft
 * Outlook), discriminate on the connection here.
 */
const calendarSource = (_calendar: Calendar): CalendarSource => ({
  name: "Google Calendar",
  icon: markRaw(GoogleCalendarIcon),
});

/**
 * The logo of the service an event comes from (its calendar's provider),
 * with a "Synced from …" tooltip. Renders nothing while the calendar is
 * unknown (still loading, or a dangling calendar id).
 */
export default defineComponent({
  props: {
    calendar: { type: Object as PropType<Calendar | null>, default: null },
  },
  setup(props) {
    const { calendar } = toRefs(props);
    const source = computed<CalendarSource | null>(() =>
      calendar.value ? calendarSource(calendar.value) : null
    );
    return { source };
  },
});
</script>

<style scoped lang="scss">
.event-source-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  // Brand logos keep their own colors; only normalize the size (the
  // .event-source-icon prefix outweighs any icon-local `svg` rule).
  svg {
    width: 14px;
    height: 14px;
  }
}
</style>
